<script>
    import { createEventDispatcher } from "svelte";
    import { notificate } from "../../services/notification.js";
    import { form as svForm, field } from "svelte-forms";
    import { required } from "svelte-forms/validators";
    import AssetsApi from "./service.js";
    import { removeTags } from "../../js/utils.js";
    import ipaddr from "ipaddr.js";
    import { format, locale } from "svelte-i18n";

    const dispatch = createEventDispatcher();

    export let kind;
    export let asset;

    let formDataPromise;
    let formData = {};
    let formIsSubmitted = false;
    // API string errors
    let APIerror = null;
    // API 422 errors
    let formApiErrors = {};
    let inputElement;
    let showformApiErrors = false;
    let is_editing = false;

    let placeholder_name = {
        domain: $format("asset.kind.domain.singular") + ".xyz",
        hostname: "host." + $format("asset.kind.domain.singular") + ".xyz",
        public_ip: "n.n.n.n",
        private_ip: "n.n.n.n",
        public_network: "n.n.n.n/n",
        private_network: "n.n.n.n/n",
        ad: "ad",
        software: "Software",
        cloud: $format("asset.kind.cloud.singular"),
        endpoint: "Endpoint",
        other: "Otro",
    };

    //form fields
    const nameField = field("name", "", [required()]);
    const myForm = svForm(nameField);

    // add form
    if (!asset) {
        formDataPromise = Promise.resolve((formData = { in_scope: true }));
    }
    // edit form
    else {
        is_editing = true;
        formData = {
            name: asset.name,
            description: asset.description,
            in_scope: asset.in_scope == "true" ? true : false,
            id: asset.id,
            kind: asset.kind,
        };
        nameField.set(formData.name);
    }

    // errors must be shown only when:
    //   field is invalid (dirty or after submit)
    //   or the API returned 422
    function shouldShowFieldError(field) {
        return (
            ((formIsSubmitted || field.dirty) && !field.valid) ||
            formApiErrors[field]
        );
    }

    const translateErrorMessages = (errorString, asset) => {
        if (errorString == "The object already exists") {
            return $format("page.asset.form.errors.object_exists");
        }
        if (errorString.includes("Domain not found")) {
            return $format("page.asset.form.errors.domain_not_found");
        }
        if (errorString == "The requested asset can not be created.") {
            return $format("page.asset.form.errors.not_created");
        }
    };

    const validateCIDR = async (formData, config) => {
        //default value avoids breaking errors
        let version = "IPv4";
        let CIDR;
        let user_input;
        if (ipaddr.isValidCIDR(formData.name)) {
            CIDR = ipaddr.parseCIDR(formData.name);
            if (CIDR[0].hasOwnProperty("octets")) {
                version = "IPv4";
            } else {
                version = "IPv6";
            }
        } else {
            APIerror = $format("page.asset.form.errors.invalid_network");
            return false;
        }

        let address = ipaddr[version].networkAddressFromCIDR(formData.name);

        if (version == "IPv4") {
            user_input = formData.name.split(".");
            user_input = user_input[3].split("/");
            if (address.octets[3] == user_input[0]) {
                return true;
            } else {
                let result = await notificate(
                    "confirm_network_from_ip",
                    "modal",
                    { network: address.octets.join("."), subnet: CIDR[1] },
                );
                if (!result.isConfirmed) {
                    return false;
                } else {
                    let networkAddress = address.toString();
                    formData.name = networkAddress + "/" + CIDR[1];
                    return true;
                }
            }
        }
        // IPv6
        else {
            address = address.toNormalizedString();
            user_input = getFullIPv6(formData.name.split("/")[0]);
            address = getFullIPv6(address.split("/")[0]);
            if (address === user_input) {
                return true;
            } else {
                let result = await notificate(
                    "confirm_network_from_ip",
                    "modal",
                    { network: address, subnet: CIDR[1] },
                );
                if (!result.isConfirmed) {
                    return false;
                } else {
                    let networkAddress = address.toString();
                    formData.name = networkAddress + "/" + CIDR[1];
                    return true;
                }
            }
        }
    };

    const getFullIPv6 = (ip_string) => {
        // replace ipv4 address if any
        let ipv4 = ip_string.match(/(.*:)([0-9]+\.[0-9]+\.[0-9]+\.[0-9]+$)/);
        if (ipv4) {
            let ip_string = ipv4[1];
            ipv4 = ipv4[2].match(/[0-9]+/g);
            for (let i = 0; i < 4; i++) {
                let byte = parseInt(ipv4[i], 10);
                ipv4[i] = ("0" + byte.toString(16)).substr(-2);
            }
            ip_string += ipv4[0] + ipv4[1] + ":" + ipv4[2] + ipv4[3];
        }

        // take care of leading and trailing ::
        ip_string = ip_string.replace(/^:|:$/g, "");

        let ipv6 = ip_string.split(":");

        for (let i = 0; i < ipv6.length; i++) {
            let hex = ipv6[i];
            if (hex != "") {
                // normalize leading zeros
                ipv6[i] = ("0000" + hex).substr(-4);
            } else {
                // normalize grouped zeros ::
                hex = [];
                for (let j = ipv6.length; j <= 8; j++) {
                    hex.push("0000");
                }
                ipv6[i] = hex.join(":");
            }
        }

        return ipv6.join(":");
    };

    // SVELTE 5 UPDATE: Added event parameter to handle preventDefault here natively
    async function handleSubmit(event) {
        console.log("submit")
        if (event) event.preventDefault();

        formIsSubmitted = true;
        // reset API errors
        formApiErrors = {};

        await nameField.validate();

        if ($myForm.hasError("name.required")) {
            notificate("422");
            return false;
        }

        formData = {
            name: $nameField.value,
            description: formData.description,
            in_scope: formData.in_scope,
            kind: kind,
        };

        for (let [k, v] of Object.entries(formData)) {
            if (typeof v == "string") {
                formData[k] = removeTags(v);
            }
        }

        if (kind == "public_network") {
            let validation = await validateCIDR(formData);
            if (!validation) return;
        }

        AssetsApi.save(formData, [], {}, !is_editing)
            .then((savedAsset) => {
                let data = [savedAsset];
                dispatch("save", { results: data });
                //clear formData and form stus and errors
                // when modal is closed after sending add request
                formData = { in_scope: true };
                formIsSubmitted = false;
                formApiErrors = {};
                return true;
            })
            .catch((errors) => {
                console.log(errors, "errors")
                if (typeof errors.detail == "string") {
                    APIerror = translateErrorMessages(errors.detail, "assets");
                } else {
                    // parse API errors
                    showformApiErrors = true;
                    if (
                        kind == "public_network" &&
                        typeof errors.detail == "object"
                    ) {
                        for (let e of errors.detail) {
                            if (e.msg.includes("The network host count")) {
                                APIerror = $format(
                                    "page.asset.form.errors.host_count",
                                );
                            }
                        }
                    } else {
                        if (
                            typeof errors == "string" &&
                            errors.includes(
                                "The workspace has reached the allowed limit",
                            )
                        ) {
                            APIerror = $format(
                                "page.asset.form.errors.limit_reached",
                            );
                        } else {
                            APIerror = $format(
                                "page.asset.form.errors.invalid_asset",
                            );
                        }
                    }
                }
                return false;
            });
        formIsSubmitted = false;
        formApiErrors = {};
        APIerror = null;
        return true;
    }
    
    function handleCloseModal() {
        formData = {};
        formIsSubmitted = false;
    }

    const handleKeydown = (event) => {
        if (event.key == "Escape") {
            handleCloseModal();
        } else {
            return;
        }
    };
</script>

<!-- SVELTE 5 UPDATE: on:keydown to onkeydown -->
<svelte:window onkeydown={handleKeydown} />

<!-- SVELTE 5 UPDATE: on:submit|preventDefault to standard onsubmit -->
<form role="form" novalidate onsubmit={handleSubmit}>
    <div class="card">
        <div class="card-body">
            {#await formDataPromise then formDataUnused}
                <div class="mb-3">
                    <label for="name">
                        {$format("page.asset.form.fields.name")}
                        <span title="Requerido" class="text-danger">*</span>
                    </label>
                    <!-- SVELTE 5 UPDATE: on:change to onchange -->
                    <input
                        type="text"
                        class="form-control t-input-asset-name"
                        id="assetAddName"
                        name="name"
                        placeholder={placeholder_name[kind]}
                        bind:value={$nameField.value}
                        disabled={formData.id}
                        onchange={async () => await nameField.validate()}
                        class:is-invalid={shouldShowFieldError($nameField)}
                    />
                    {#if APIerror}
                        <div class="mt-3 text-danger" style="font-size: 80%;">
                            {APIerror}
                        </div>
                    {/if}
                    {#if shouldShowFieldError($nameField)}
                        {#if $myForm.hasError("name.required")}
                            <div class="invalid-feedback">
                                {$format("required")}
                            </div>
                        {/if}
                    {/if}
                </div>

                <div class="mb-3">
                    <label for="description">
                        {$format("page.asset.form.fields.description")}
                    </label>
                    <input
                        type="text"
                        class="form-control t-input-asset-description"
                        id="assetAddDescription"
                        name="description"
                        placeholder={$format("form.fields.description")}
                        bind:value={formData.description}
                    />
                </div>
                <div class="mb-3">
                    {#if asset && asset.third_party}
                        <div class="alert alert-warning mt-3" role="alert">
                            {$format("page.asset.form.warnings.third_party")}
                            {asset.third_party}.
                        </div>
                    {:else}
                        <div class="form-check form-switch">
                            <input
                                type="checkbox"
                                bind:this={inputElement}
                                bind:checked={formData.in_scope}
                                class="form-check-input"
                                id={"switch_in_scope" + formData.id}
                            />
                            <label
                                class="custom-control-label"
                                for={"switch_in_scope" + formData.id}
                            ></label>{$format(
                                "page.asset.form.fields.in_scope",
                            )}
                        </div>
                    {/if}
                    {#if !formData.in_scope && !asset}
                        <!--@TODO: componentizar -->
                        <div class="alert alert-warning mt-3" role="alert">
                            {#if !asset}
                                {$format(
                                    "page.asset.form.warnings.out_of_scope",
                                )}
                            {/if}
                        </div>
                    {/if}
                </div>
            {/await}
        </div>
        <div class="card-footer">
            <button
                id="assetAddSubmit"
                type="submit"
                class="btn btn-primary t-submit-asset-btn"
            >{$format("buttons.save")}</button>
            <button
                type="button"
                class="btn btn-default"
                onclick={handleCloseModal}
                data-bs-dismiss="modal"
            >
                {$format("buttons.cancel")}
            </button>
        </div>
        {#await formDataPromise}
            <div class="overlay">
                <i class="fas fa-2x fa-sync-alt fa-spin" />
            </div>
        {/await}
    </div>
</form>

<style>
    .alert.alert-warning {
        color: #856404;
        background-color: #fff3cd;
    }
</style>